import React, { useState, useEffect } from 'react';
import { Box, Button, CircularProgress, Typography, Alert, Modal, Chip } from '@mui/material';
import Script from 'next/script';
import { getBackendURL } from '../../lib/util';
import GoogleIcon from '@mui/icons-material/Google';
import { VenueGoogleCalendarAccess } from '../../../../shared/models';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../lib/firebase/app';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

const SCOPES = ['https://www.googleapis.com/auth/calendar.readonly'];
const CLIENT_ID = '1068798323193-duapt8n8e3vmdgsm5670vftntrsr0psf.apps.googleusercontent.com'

const GoogleCalendarConnect: React.FC<{ venueId: string }> = ({ venueId }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isCheckingConnection, setIsCheckingConnection] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isScriptLoaded, setIsScriptLoaded] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [calendarAccesses, setCalendarAccesses] = useState<VenueGoogleCalendarAccess[]>([]);
    const [connectedCalendars, setConnectedCalendars] = useState<string[]>([]);

    // Check for existing calendar connections on mount
    useEffect(() => {
        const checkExistingConnections = async () => {
            if (!venueId) return;

            setIsCheckingConnection(true);
            try {
                const venueGoogleCalendarAccessRef = collection(db, 'venue_google_calendar_access');
                const q = query(venueGoogleCalendarAccessRef, where('VenueID', '==', venueId));
                const querySnapshot = await getDocs(q);

                if (!querySnapshot.empty) {
                    const accesses = querySnapshot.docs.map(doc => doc.data() as VenueGoogleCalendarAccess);
                    setCalendarAccesses(accesses);

                    // Get the list of connected calendars
                    const calendars: string[] = [];
                    accesses.forEach(access => {
                        if (access.SelectedCalendarIDs && access.SelectedCalendarIDs.length > 0) {
                            access.SelectedCalendarIDs.forEach(calId => {
                                const calName = access.CalendarIDtoName?.[calId] || calId;
                                calendars.push(calName);
                            });
                        } else if (access.CalendarID && access.CalendarIDtoName) {
                            const calName = access.CalendarIDtoName[access.CalendarID] || access.CalendarID;
                            calendars.push(calName);
                        }
                    });

                    setConnectedCalendars(calendars);
                    setIsConnected(accesses.length > 0);
                } else {
                    setIsConnected(false);
                }
            } catch (error) {
                console.error('Error checking calendar connections:', error);
                setError('Failed to check calendar connections');
            } finally {
                setIsCheckingConnection(false);
            }
        };

        checkExistingConnections();
    }, [venueId]);

    useEffect(() => {
        console.log('Checking if Google script is loaded');
        if (window.google) {
            console.log('Google script is loaded');
            setIsScriptLoaded(true);
        } else {
            console.log('Google script is not loaded');
        }
    }, []);

    const handleConnect = () => {
        console.log('handleConnect called');
        if (!isScriptLoaded) {
            console.log('Script not loaded, loading now');
            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            script.onload = () => {
                console.log('Google script loaded');
                setIsScriptLoaded(true);
                initializeGoogleClient();
            };
            document.body.appendChild(script);
            return;
        }
        initializeGoogleClient();
    };

    const initializeGoogleClient = () => {
        console.log('Setting isLoading to true');
        setIsLoading(true);
        console.log('Initializing Google OAuth2 client');

        const REDIRECT_URI = process.env.NODE_ENV === 'development'
            ? 'https://www.getlitty.com/venue_admin?id=' + venueId
            : `https://www.getlitty.com/venue_admin?id=` + venueId;

        const client = (window as any).google.accounts.oauth2.initCodeClient({
            client_id: CLIENT_ID,
            scope: SCOPES.join(' '),
            redirect_uri: REDIRECT_URI,
            access_type: 'offline',
            ux_mode: 'popup',
            callback: async (response) => {
                if (response.code) {
                    try {
                        const result = await fetch(getBackendURL('getGoogleCalendarToken'), {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ code: response.code, venueId: venueId }),
                        });
                        const data = await result.json();
                        if (data.error) {
                            throw new Error(data.error);
                        }
                        setIsConnected(true);
                        // Refresh the page to show updated calendar status
                        window.location.reload();
                        // dismiss modal
                        handleCloseModal();
                    } catch (error) {
                        console.error('Error exchanging code for tokens:', error);
                        setError('Failed to connect Google Calendar. Please try again.');
                    } finally {
                        setIsLoading(false);
                    }
                }
            }
        });
        client.requestCode();
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    // Show loading state while checking connections
    if (isCheckingConnection) {
        return (
            <Button
                variant="contained"
                size="small"
                disabled
                sx={{
                    backgroundColor: '#C0C0DD',
                    color: 'black',
                    fontFamily: 'Inter',
                    fontSize: '0.875rem',
                    padding: '6px 16px',
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: 'none',
                }}
            >
                <CircularProgress size={16} sx={{ mr: 1, color: 'black' }} />
                Checking...
            </Button>
        );
    }

    // Show connected status if calendars are connected
    if (isConnected && connectedCalendars.length > 0) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                    variant="contained"
                    size="small"
                    sx={{
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        fontFamily: 'Inter',
                        fontSize: '0.875rem',
                        padding: '6px 16px',
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: '#45a049',
                        },
                    }}
                    startIcon={<CheckCircleIcon />}
                    onClick={handleOpenModal}
                >
                    {connectedCalendars.length} Calendar{connectedCalendars.length > 1 ? 's' : ''} Connected
                </Button>

                <Modal
                    open={isModalOpen}
                    onClose={handleCloseModal}
                >
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        display: 'flex',
                        flexDirection: 'column',
                        p: 4,
                        bgcolor: 'rgba(26, 26, 46, 0.95)',
                        backdropFilter: 'blur(8px)',
                        color: 'white',
                        border: '1px solid rgba(224, 177, 241, 0.3)',
                        borderRadius: '16px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                        maxWidth: { xs: '95%', sm: 500 },
                        width: '100%',
                        margin: 'auto',
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <CheckCircleIcon sx={{ color: '#4CAF50', mr: 2 }} />
                            <Typography variant="h5" component="h2" fontFamily='Roboto Condensed' sx={{ fontWeight: 600 }}>
                                Calendar Connected!
                            </Typography>
                        </Box>

                        <Typography variant="body1" sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.9)', lineHeight: 1.6 }} fontFamily='Inter'>
                            Your Google Calendar is successfully connected. Connected calendars:
                        </Typography>

                        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {connectedCalendars.map((calName, index) => (
                                <Chip
                                    key={index}
                                    label={calName}
                                    icon={<CalendarMonthIcon />}
                                    sx={{
                                        backgroundColor: 'rgba(76, 175, 80, 0.2)',
                                        color: '#4CAF50',
                                        border: '1px solid rgba(76, 175, 80, 0.3)',
                                        '& .MuiChip-icon': {
                                            color: '#4CAF50'
                                        },
                                        '& .MuiChip-label': {
                                            fontFamily: 'Inter',
                                        }
                                    }}
                                />
                            ))}
                        </Box>

                        <Typography variant="body2" sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.5 }} fontFamily='Inter'>
                            We're automatically syncing your calendar events and making educated guesses for potential bookings.
                            You can manage your calendar settings by clicking the settings icon.
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button
                                variant="outlined"
                                onClick={handleCloseModal}
                                sx={{
                                    color: '#C0C0DD',
                                    borderColor: 'rgba(224, 177, 241, 0.5)',
                                    fontFamily: 'Inter',
                                    fontWeight: 600,
                                    '&:hover': {
                                        borderColor: '#C0C0DD',
                                        backgroundColor: 'rgba(224, 177, 241, 0.1)'
                                    }
                                }}
                            >
                                Close
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    handleCloseModal();
                                    // Trigger the connection flow for adding another calendar
                                    setTimeout(() => {
                                        handleConnect();
                                    }, 100);
                                }}
                                disabled={isLoading}
                                sx={{
                                    backgroundColor: '#C0C0DD',
                                    color: 'black',
                                    fontFamily: 'Inter',
                                    fontWeight: 600,
                                    '&:hover': {
                                        backgroundColor: '#D8A8E8',
                                    },
                                    '&:disabled': {
                                        backgroundColor: 'rgba(192, 192, 221, 0.6)',
                                        color: 'rgba(0, 0, 0, 0.6)',
                                    }
                                }}
                            >
                                {isLoading ? 'Connecting...' : 'Add Another Calendar'}
                            </Button>
                        </Box>
                    </Box>
                </Modal>
            </Box>
        );
    }

    // Show connect button if no calendars are connected
    return (
        <>
            <Script
                src="https://accounts.google.com/gsi/client"
                strategy="beforeInteractive"
                onLoad={() => setIsScriptLoaded(true)}
            />
            <Button
                onClick={handleOpenModal}
                variant="contained"
                size="small"
                sx={{
                    backgroundColor: '#C0C0DD',
                    color: 'black',
                    fontFamily: 'Inter',
                    fontSize: '0.875rem',
                    padding: '6px 16px',
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: 'none',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        backgroundColor: '#D8A8E8',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    },
                    '&:focus': {
                        outline: 'none',
                        boxShadow: '0 0 0 2px rgba(224,177,241,0.5)',
                    },
                }}
            >
                Connect Calendar
            </Button>

            <Modal
                open={isModalOpen}
                onClose={handleCloseModal}
                aria-labelledby="google-calendar-connect-modal"
                aria-describedby="modal-to-connect-google-calendar"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 4,
                    bgcolor: 'rgba(26, 26, 46, 0.95)',
                    backdropFilter: 'blur(8px)',
                    color: 'white',
                    border: '1px solid rgba(224, 177, 241, 0.3)',
                    borderRadius: '16px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    maxWidth: { xs: '95%', sm: 450 },
                    width: '100%',
                    margin: 'auto',
                }}>
                    <Typography variant="h4" component="h2" gutterBottom fontFamily='Roboto Condensed' sx={{ mb: 3, textAlign: 'center', fontWeight: 600, color: '#C0C0DD' }}>
                        Connect A Calendar
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 3, textAlign: 'center', color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.6 }} fontFamily='Inter'>
                        We'll sync calendar events that we're sure are bookings and make educated guesses for events we're not certain about. This saves you time - you'll just need to approve our guesses!
                    </Typography>
                    {error && (
                        <Box sx={{
                            mb: 3,
                            width: '100%',
                            p: 2,
                            bgcolor: 'rgba(255, 82, 82, 0.1)',
                            border: '1px solid rgba(255, 82, 82, 0.3)',
                            borderRadius: '8px'
                        }}>
                            <Typography variant="body2" sx={{ color: '#ff5252' }} fontFamily='Inter'>
                                {error}
                            </Typography>
                        </Box>
                    )}
                    <Button
                        onClick={handleConnect}
                        disabled={isLoading}
                        startIcon={isLoading ? <CircularProgress size={20} sx={{ color: 'rgba(255, 255, 255, 0.7)' }} /> : <GoogleIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />}
                        variant="contained"
                        fullWidth
                        sx={{
                            backgroundColor: '#C0C0DD',
                            color: 'black',
                            fontFamily: 'Inter',
                            fontSize: '1rem',
                            padding: '14px 20px',
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontWeight: 600,
                            boxShadow: 'none',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                                backgroundColor: '#D8A8E8',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                            },
                            '&:focus': {
                                outline: 'none',
                                boxShadow: '0 0 0 3px rgba(224,177,241,0.5)',
                            },
                            '&:disabled': {
                                backgroundColor: 'rgba(192, 192, 221, 0.6)',
                                color: 'rgba(0, 0, 0, 0.6)',
                            }
                        }}
                    >
                        {isLoading ? 'Connecting...' : 'Connect Google Calendar'}
                    </Button>
                    <Button
                        variant="text"
                        sx={{
                            color: '#C0C0DD',
                            fontFamily: 'Inter',
                            fontSize: '0.875rem',
                            marginTop: 2,
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: 'rgba(224,177,241,0.1)',
                            },
                        }}
                        onClick={() => window.location.href = 'mailto:support@getlitty.com?subject=Additional Calendar Connection Request'}
                    >
                        Have another type of calendar? Send us an email and we'll set it up for you!
                    </Button>
                </Box>
            </Modal>
        </>
    );
};

export default GoogleCalendarConnect;
